import { Injectable } from '@angular/core';
import {
    ChangePasswordRequest,
    CordIamService,
    GenerateSSOLoginUrlRequest,
    GetUserResponse,
    LoginResponse,
    PasswordResetRequest,
    PasswordResetTokenRequest
} from '@yellowtail-frontend/cord-connector';
import { StorageService } from '@yellowtail-frontend/storage-service';
import { StorageObjects } from './../../../app/utilities/enum';
import { tenant } from '../../../tenants/tenant';
import { environment } from '../../../environments/environment';
import { HelperService } from '../utilities/helper.service';
import jwt_decode from 'jwt-decode';
import { UserService } from './user.service';
import { VariableService } from '@utilServices/variables.service';

@Injectable({
    providedIn: 'root'
})
export class LoginService {

    constructor(
        private readonly _storageService: StorageService,
        private readonly _cordIamService: CordIamService,
        private readonly _helperService: HelperService,
        private readonly _userService: UserService,
        private readonly _variableService: VariableService
    ) {}

    loginUser(username: string, password: string): Promise<LoginResponse> {
        return new Promise<LoginResponse>((resolve, reject) => {
            this._cordIamService.login({ Username: username, Password: password })
                .then(response => {
                    if (response?.Token !== undefined) {
                        this._storageService.set(StorageObjects.token, response.Token);
                        this._storageService.set(StorageObjects.userId, response.UserID);
                        this._variableService.userID = response.UserID;
                        this._storageService.set(StorageObjects.userType, response.Type);
                        this._helperService.appendToken();
                        resolve(response);
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    generateSecretFromEmailToken(emailToken: string): Promise<string> {
        return this._cordIamService.get(`TwoFactorAuth/generateSecret?emailToken=${encodeURIComponent(emailToken)}`);
    }

    async generateRedirectLoginUrl(): Promise<string> {
        return new Promise<string>((resolve) => {
            const PROVIDER: GenerateSSOLoginUrlRequest = {
                Provider: environment.ssoProvider
            };
            resolve(this._cordIamService.generateSSOLoginUrl(PROVIDER));
        });
    }

    forcedPasswordReset(passwordRequest: ChangePasswordRequest): Promise<void> {
        return this._cordIamService.forcedPasswordReset({
            Username: passwordRequest.Username,
            OldPassword: passwordRequest.OldPassword,
            NewPassword: passwordRequest.NewPassword
        });
    }

    resetPassword(data: PasswordResetRequest): Promise<void> {
        return this._cordIamService.resetPassword(data);
    }

    forgotPassword(data: PasswordResetTokenRequest): Promise<any> {
        return this._cordIamService.requestResetPassword(data);
    }

    async loginUserWithOAuthCode(code: string): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            this._cordIamService.loginWithOAuthCode(tenant.oAuthProvider, code).then((response: LoginResponse) => {
                const decoded: { context, sub } = jwt_decode(response.Token);
                this._storageService.set(StorageObjects.token, response.Token);
                this._storageService.set(StorageObjects.userId, decoded.sub);
                this._storageService.set(StorageObjects.userType, decoded?.context?.type);

                this._helperService.appendToken();

                this.getUserProfile(decoded).then(() => {
                    resolve(response.Token);
                });
            }).catch(error => {
                reject(error);
            });
        });
    }

    async authenticateUser(username: string, password: string): Promise<any> {
        return new Promise((resolve, reject) => {
            resolve({
                success: true,
                response: 'string',
            });
        });
    }

    logout(): Promise<any> {
        return new Promise((resolve, reject) => {
            this._cordIamService.logout().then(response => {
                if (response === '') {
                    resolve(response);
                }
            });
        });
    }

    private getUserProfile(decodedToken: any): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this._storageService.set(StorageObjects.userOrganizationId, decodedToken?.context?.organization ? decodedToken.context.organization : '');
            this._userService.getCurrentUser()
                .then((userResponse: GetUserResponse) => {
                    this._userService.userProfile = userResponse.Profile;
                    this._storageService.set(StorageObjects.userEmail, userResponse.Profile.Email);
                    this._storageService.set(StorageObjects.userProfile, JSON.stringify(userResponse.Profile));
                    this._storageService.set(StorageObjects.userRole, userResponse.Roles[0]);
                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
        });
    }
}
